import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import { Paper, Typography, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMst } from '../../core/stores';
import { productSubforms } from './product-form-schemas';
import {
  products,
  visionProducts,
  integrations,
  isFacilityKeyedProductDetail,
} from '../../libs/static/productsAndIntegrations.enum';
import { isEmpty } from 'lodash-es';
import { OrderBreadcrumbs } from '../../components/OrderBreadcrumbs/OrderBreadcrumbs';
import { Product } from '../../components/Product/Product';
import { reindexFacilityDetails } from '../../libs/helpers/facilityKeys';
import { mergeProductDetails } from './productDetailsUtils';

export interface DOProductProps extends RouteComponentProps<{ id: string }> {}

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'flex',
    width: '88%',
    flexShrink: 0,
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  formContainer: {
    flexGrow: 1,
    height: '100%',
    margin: theme.spacing(2),
    '& > *': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    overflowX: 'auto',
  },
  summary: {
    margin: 0,
  },
  buttonGroup: {
    width: '100%',
  },
  button: {
    width: '50%',
  },
  title: {
    width: '100%',
  },
  sectionTitle: {
    width: '100%',
    marginTop: 0,
    marginBottom: 0,
  },
  buttomButtonRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  actionButtons: {
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
  usdNote: {
    color: theme.palette.primary.main,
  },
}));

export const ProductForm: React.FC<DOProductProps> = observer(({ match }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const orderId = match.params.id;
  const { digitalOrdersStore, navigationStore } = useMst();
  const currentOrder: any = digitalOrdersStore.order(orderId);
  const [disabledBtn, setDisabledBtn] = useState(true);
  const isVision = import.meta.env.VITE_DO_MODE === 'vision';
  const productOptions = isVision ? visionProducts : products;

  // Product renders the facility grid off the same names, so resolve them the same way it does.
  const facilityNames: string[] = (currentOrder.facility?.facilities ?? []).map(f =>
    f.facilityId ? digitalOrdersStore.facilityMap.get(f.facilityId)?.name ?? '' : f.name,
  );

  const makeProductDataBundle = (order, productName, context) => {
    const bundle: any = {
      checkbox:
        context === 'product'
          ? order.products?.productSelection[productName] || false
          : order.products?.integrations[productName] || false,
    };
    if (order.productDetails && order.productDetails[productName]) {
      // Canonicalize facility keys here, not just in Product: this bundle also backs the
      // Next-button validity check and submit, so leaving the raw (possibly humps-mangled)
      // keys in place would re-persist them and disagree with what the grid shows (DO-295).
      bundle.subform = isFacilityKeyedProductDetail(productName)
        ? reindexFacilityDetails(order.productDetails[productName], facilityNames)
        : order.productDetails[productName];
      bundle.subformValid = true;
    }
    return bundle;
  };

  const extractInitialData = (order, context) => {
    let data: any = {};
    const orderKey = context === 'product' ? 'productSelection' : 'integrations';
    if (order.products && order.products[orderKey]) {
      data = Object.keys(order.products[orderKey]).reduce((memo, key) => {
        memo[key] = makeProductDataBundle(order, key, context);
        return memo;
      }, {});
    }
    return data;
  };

  const [productData, setProductData] = useState(extractInitialData(currentOrder, 'product'));
  const [integrationData, setIntegrationData] = useState(
    extractInitialData(currentOrder, 'integration'),
  );

  useEffect(() => {
    const productsChecked = Object.keys(productData).filter(key => productData[key].checkbox);
    const integrationsChecked = Object.keys(integrationData).filter(
      key => integrationData[key].checkbox,
    );
    const atLeastOneSelection = productsChecked.length + integrationsChecked.length > 0;
    const productSubformsValid = Object.keys(productData).reduce((memo, curr) => {
      // Vision AM requires at least one console (Touchscreen or LED) entered across facilities.
      const amVisionValid =
        curr === 'AM_VISION'
          ? Object.values(productData[curr].subform || {}).some((facility: any) => {
              return (
                !!facility &&
                (+facility.amVisionTouchscreenCount > 0 || +facility.amVisionLEDCount > 0)
              );
            })
          : true;
      return productData[curr].checkbox && productData[curr].hasOwnProperty('subformValid')
        ? memo && productData[curr].subformValid && amVisionValid
        : memo;
    }, true);
    const integrationSubformsValid = Object.keys(integrationData).reduce((memo, curr) => {
      const iFitValid =
        curr === 'iFit' ? Object.values(integrationData[curr].subform).some(val => +val) : true;
      return integrationData[curr].checkbox && integrationData[curr].hasOwnProperty('subformValid')
        ? memo && integrationData[curr].subformValid && iFitValid
        : memo;
    }, true);
    setDisabledBtn(!(atLeastOneSelection && productSubformsValid && integrationSubformsValid));
  }, [productData, integrationData]);

  const handleProductChange = evt => {
    const key = Object.keys(evt)[0];
    setProductData(
      Object.assign({}, productData, {
        [key]: Object.assign({}, productData[key], evt[key]),
      }),
    );
  };

  const handleIntegrationChange = evt => {
    const key = Object.keys(evt)[0];
    const data = !isEmpty(integrationData) ? integrationData : evt;
    setIntegrationData({
      ...data,
      [key]: {
        ...data[key],
        ...evt[key],
        subform: isEmpty(evt[key].subform) ? {} : { ...evt[key].subform },
      },
    });
  };

  const extractValues = (dataObj, target) => {
    return Object.keys(dataObj).reduce((memo, key) => {
      if (target === 'checkbox' && dataObj[key].checkbox) {
        memo[key] = true;
      } else if (target === 'subform' && dataObj[key].checkbox && dataObj[key].subform) {
        memo[key] = dataObj[key].subform;
      }
      return memo;
    }, {});
  };

  const submit = () => {
    const subForms = {
      ...extractValues(productData, 'subform'),
      ...extractValues(integrationData, 'subform'),
    };
    const nextSelection = {
      productSelection: extractValues(productData, 'checkbox'),
      integrations: extractValues(integrationData, 'checkbox'),
    };
    // Always write productDetails — merged, never replaced, so the Product Order Details step's
    // values survive a trip back through Products, and so deselecting a product still persists.
    const updateObj = {
      products: nextSelection,
      // `productDetails` is declared Record<string, string> on the order model but holds nested
      // objects at runtime (same mismatch exportFulfilledOrders.ts documents). Cast at the
      // boundary rather than widening the model, which AutomationActionUtils relies on.
      productDetails: mergeProductDetails({
        existingDetails: currentOrder.productDetails,
        previousSelection: currentOrder.products,
        nextSelection,
        subForms,
      }) as Record<string, string>,
    };

    digitalOrdersStore.writeToCurrentOrder(orderId, updateObj);
    digitalOrdersStore.submitDigitalOrder(orderId);
    navigationStore.push(`/digital-orders/${orderId}/product-details`);
  };

  const back = () => {
    navigationStore.push(`/digital-orders/${orderId}/facilities`);
  };

  return (
    <Paper className={classes.paper}>
      <div className={classes.formContainer}>
        <Typography className={classes.title} variant="h5">
          Select Products
        </Typography>
        <Typography className={classes.usdNote} variant="subtitle2">
          All prices are US list prices
        </Typography>
        <Typography className={classes.sectionTitle} variant="h6">
          Products
        </Typography>
        {productOptions.map(prod => {
          return (
            <Product
              key={prod.value}
              name={prod.value}
              title={prod.label}
              onChange={handleProductChange}
              values={makeProductDataBundle(currentOrder, prod.value, 'product')}
              subFormSchema={productSubforms[prod.value]}
              facilities={currentOrder.facility.facilities}
            ></Product>
          );
        })}
        {!isVision && (
          <>
            <Typography className={classes.sectionTitle} variant="h6">
              Integrations
            </Typography>
            {integrations.map(int => {
              return (
                <Product
                  key={int.value}
                  name={int.value}
                  title={int.label}
                  onChange={handleIntegrationChange}
                  values={makeProductDataBundle(currentOrder, int.value, 'integration')}
                  subFormSchema={productSubforms[int.value]}
                  facilities={currentOrder.facility.facilities}
                ></Product>
              );
            })}
          </>
        )}
        <div className={classes.buttomButtonRow}>
          <Button variant="contained" color="secondary" onClick={back}>
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={submit} disabled={disabledBtn}>
            Next
          </Button>
        </div>
      </div>
      <OrderBreadcrumbs className={classes.summary} orderId={orderId} />
    </Paper>
  );
});
